import { useEffect, useState } from "react";

const MARK = "/logo-mark.png";

export default function Splash({ onDone }) {
  const [progress, setProgress] = useState(0);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const DURATION = 1900;
    const start = Date.now();
    let raf;
    let finished = false;

    const finish = () => {
      if (finished) return;
      finished = true;
      setExiting(true);
      window.setTimeout(() => onDone && onDone(), 550);
    };

    const tick = () => {
      const elapsed = Date.now() - start;
      setProgress(Math.min(100, (elapsed / DURATION) * 100));
      if (elapsed >= DURATION) {
        finish();
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    // Fallback timer (fires even if rAF is paused)
    const hardStop = window.setTimeout(finish, 2600);

    // Skip on first user interaction (robust even when timers are throttled)
    const skip = () => finish();
    window.addEventListener("pointerdown", skip, { once: true });
    window.addEventListener("keydown", skip, { once: true });
    window.addEventListener("wheel", skip, { once: true, passive: true });
    window.addEventListener("touchstart", skip, { once: true, passive: true });

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(hardStop);
      window.removeEventListener("pointerdown", skip);
      window.removeEventListener("keydown", skip);
      window.removeEventListener("wheel", skip);
      window.removeEventListener("touchstart", skip);
    };
  }, [onDone]);

  return (
    <div
      data-testid="splash-screen"
      className={`splash-root pointer-events-none fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-[#e9eef5] ${
        exiting ? "is-exiting" : ""
      }`}
    >
      <div className="pointer-events-none absolute h-[520px] w-[520px] rounded-full bg-blue-600/20 blur-[120px]" />

      <div className="relative z-10 flex flex-col items-center" style={{ perspective: 1000 }}>
        <img
          src={MARK}
          alt="StratFlow Tech"
          className="splash-mark animate-float w-[140px] drop-shadow-[0_8px_22px_rgba(15,23,42,0.22)] md:w-[180px]"
        />
        <div className="splash-text mt-6 text-center">
          <div className="font-display text-3xl font-extrabold tracking-tight text-slate-900 md:text-4xl">
            STRAT<span className="text-blue-600"> FLOW</span>
          </div>
          <div className="mt-2 font-mono-x text-[10px] tracking-[0.5em] text-slate-600 md:text-xs">
            TECH FZCO
          </div>
        </div>
      </div>

      <div className="splash-bar relative z-10 mt-10 w-[220px]">
        <div className="h-[3px] w-full overflow-hidden rounded-full bg-white/10">
          <div
            className="h-full rounded-full bg-gradient-to-r from-blue-400 to-blue-600 transition-[width] duration-150 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="mt-3 flex justify-between font-mono-x text-[10px] uppercase tracking-[0.25em] text-slate-500">
          <span>Initialising</span>
          <span>{Math.round(progress)}%</span>
        </div>
      </div>
    </div>
  );
}
